/**
 * Tool Executions Page
 * ინსტრუმენტების შესრულების სტატისტიკა
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/formatDate';
import {
  Card,
  Button,
  DataTable,
  Modal,
  Spinner,
} from '@aiia/portal-core';
import type { TableColumn } from '@aiia/portal-core';
import { useToolExecutions, useToolExecutionStats } from '../hooks/useToolExecutions';
import type { ToolExecutionLog } from '../features/api/toolExecutions';

export default function ToolExecutionsPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [period, setPeriod] = useState<string>('');
  const [detailModal, setDetailModal] = useState<ToolExecutionLog | null>(null);

  const { data: executions, isLoading: execLoading } = useToolExecutions(page, 20);
  const { stats, isLoading: statsLoading } = useToolExecutionStats(period || undefined);

  const columns: TableColumn<ToolExecutionLog & Record<string, unknown>>[] = [
    {
      key: 'toolName',
      header: t('toolExecutions.toolName'),
      render: (value) => <span className="font-medium">{String(value)}</span>,
    },
    {
      key: 'success',
      header: t('common.status'),
      render: (value) => (
        <span className={`px-2 py-0.5 text-xs rounded-full ${
          value ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {value ? t('common.success') : t('common.failed')}
        </span>
      ),
    },
    {
      key: 'httpStatus',
      header: 'HTTP',
      render: (value) => value ? String(value) : '-',
    },
    {
      key: 'executionTimeMs',
      header: t('toolExecutions.executionTime'),
      render: (value) => value ? `${value}ms` : '-',
    },
    {
      key: 'executedAt',
      header: t('toolExecutions.executedAt'),
      render: (value) => formatDate(String(value)),
    },
    {
      key: 'actions',
      header: '',
      render: (_, row) => (
        <Button size="sm" variant="secondary" onClick={() => setDetailModal(row as unknown as ToolExecutionLog)}>
          {t('toolExecutions.details')}
        </Button>
      ),
    },
  ];

  if (execLoading && statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const successRate = stats && stats.totalExecutions > 0
    ? Math.round((stats.successCount / stats.totalExecutions) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('toolExecutions.title')}</h1>
        <p className="text-gray-600 mt-1">{t('toolExecutions.subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-500">{t('toolExecutions.totalExecutions')}</p>
            <p className="text-2xl font-bold">{stats?.totalExecutions ?? 0}</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-500">{t('toolExecutions.successRate')}</p>
            <p className="text-2xl font-bold text-green-600">{successRate}%</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-500">{t('toolExecutions.failures')}</p>
            <p className="text-2xl font-bold text-red-600">{stats?.failureCount ?? 0}</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-500">{t('toolExecutions.avgTime')}</p>
            <p className="text-2xl font-bold">{stats?.avgExecutionTimeMs ? `${Math.round(stats.avgExecutionTimeMs)}ms` : '-'}</p>
          </div>
        </Card>
      </div>

      {/* Period Filter */}
      <div className="flex gap-2">
        {['', 'today', 'week', 'month'].map(p => (
          <Button
            key={p}
            size="sm"
            variant={period === p ? 'primary' : 'secondary'}
            onClick={() => setPeriod(p)}
          >
            {p === '' ? t('toolExecutions.allTime') : t(`toolExecutions.${p}`)}
          </Button>
        ))}
      </div>

      {/* Per-Tool Stats */}
      {stats && stats.byTool.length > 0 && (
        <Card>
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-3">{t('toolExecutions.byTool')}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-2">{t('toolExecutions.toolName')}</th>
                    <th className="pb-2">{t('toolExecutions.totalExecutions')}</th>
                    <th className="pb-2">{t('common.success')}</th>
                    <th className="pb-2">{t('common.failed')}</th>
                    <th className="pb-2">{t('toolExecutions.avgTime')}</th>
                    <th className="pb-2">{t('toolExecutions.successRate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.byTool.map(ts => (
                    <tr key={ts.toolName} className="border-b">
                      <td className="py-2 font-medium">{ts.toolName}</td>
                      <td className="py-2">{ts.totalExecutions}</td>
                      <td className="py-2 text-green-600">{ts.successCount}</td>
                      <td className="py-2 text-red-600">{ts.failureCount}</td>
                      <td className="py-2">{ts.avgExecutionTimeMs ? `${Math.round(ts.avgExecutionTimeMs)}ms` : '-'}</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          ts.totalExecutions > 0 && (ts.successCount / ts.totalExecutions) >= 0.9
                            ? 'bg-green-100 text-green-700'
                            : ts.totalExecutions > 0 && (ts.successCount / ts.totalExecutions) >= 0.5
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                        }`}>
                          {ts.totalExecutions > 0 ? Math.round((ts.successCount / ts.totalExecutions) * 100) : 0}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {/* Execution History */}
      <Card>
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-3">{t('toolExecutions.history')}</h3>
        </div>
        <DataTable
          columns={columns}
          data={(executions?.content || []) as (ToolExecutionLog & Record<string, unknown>)[]}
          emptyMessage={t('toolExecutions.emptyMessage')}
        />
        {executions && executions.totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              {t('toolExecutions.previous')}
            </Button>
            <span className="px-3 py-1 text-sm text-gray-600">
              {page + 1} / {executions.totalPages}
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= executions.totalPages - 1}
            >
              {t('toolExecutions.next')}
            </Button>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal
        isOpen={!!detailModal}
        onClose={() => setDetailModal(null)}
        title={t('toolExecutions.executionDetails')}
      >
        {detailModal && (
          <div className="space-y-3 text-sm">
            <div>
              <span className="font-medium text-gray-500">{t('toolExecutions.toolName')}:</span>{' '}
              <span className="font-semibold">{detailModal.toolName}</span>
            </div>
            <div>
              <span className="font-medium text-gray-500">{t('common.status')}:</span>{' '}
              <span className={detailModal.success ? 'text-green-600' : 'text-red-600'}>
                {detailModal.success ? t('common.success') : t('common.failed')}
              </span>
            </div>
            {detailModal.httpStatus && (
              <div>
                <span className="font-medium text-gray-500">HTTP Status:</span>{' '}
                {detailModal.httpStatus}
              </div>
            )}
            {detailModal.executionTimeMs != null && (
              <div>
                <span className="font-medium text-gray-500">{t('toolExecutions.executionTime')}:</span>{' '}
                {detailModal.executionTimeMs}ms
              </div>
            )}
            {detailModal.webhookUrl && (
              <div>
                <span className="font-medium text-gray-500">Webhook URL:</span>{' '}
                <span className="break-all">{detailModal.webhookUrl}</span>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-500">{t('toolExecutions.executedAt')}:</span>{' '}
              {formatDate(detailModal.executedAt)}
            </div>
            {detailModal.inputParameters && Object.keys(detailModal.inputParameters).length > 0 && (
              <div>
                <span className="font-medium text-gray-500">{t('toolExecutions.inputParameters')}:</span>
                <pre className="mt-1 p-2 bg-gray-50 rounded text-xs overflow-auto max-h-40">
                  {JSON.stringify(detailModal.inputParameters, null, 2)}
                </pre>
              </div>
            )}
            {detailModal.responseBody && (
              <div>
                <span className="font-medium text-gray-500">{t('toolExecutions.response')}:</span>
                <pre className="mt-1 p-2 bg-gray-50 rounded text-xs overflow-auto max-h-40">
                  {detailModal.responseBody}
                </pre>
              </div>
            )}
            {detailModal.errorMessage && (
              <div>
                <span className="font-medium text-gray-500">{t('toolExecutions.errorMessage')}:</span>
                <pre className="mt-1 p-2 bg-red-50 rounded text-xs text-red-700 overflow-auto max-h-40">
                  {detailModal.errorMessage}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
